/**
 * market-reviews-api — typed wrapper for the S2-H9 seller-reply endpoint.
 *
 * Root cause context (S2-H9 — SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03
 * item H-9): sellers had no way to post a public response to a
 * `buyer_to_seller` review. This module exposes the frontend caller for
 * the new `POST /api/market/orders/:orderNumber/reviews/reply` endpoint.
 *
 * Why a separate module (NOT inside `frontend/src/lib/api-client.ts`):
 * `api-client.ts` is the #1 frontend god-file (9.513 lines — registered
 * in TECH-DEBT-REGISTER §2E). CR-CHECKLIST I2 forbids growing god-files
 * in a PR. The market-reviews feature has a small surface (1 method
 * today), so a dedicated thin wrapper is the correct structural choice.
 * When the api-client god-file is split (future PR per
 * TECH-DEBT-REGISTER plan), this module can be merged into the resulting
 * market-domain endpoint file.
 *
 * §6 Anti-Invención: NO business logic — thin typed wrapper around
 * `apiFetch`. The 30-day window + 1-reply invariant + caller-is-seller
 * check all live in the backend `review.service.respondToReview`.
 *
 * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item H-9 (seller
 *           reply reviews — fix de raíz).
 */

import { apiFetch, ApiClientError } from './api-client';
import type { SellerReply } from '@profileia/shared';

// ─── Response shape ──────────────────────────────────────────────────────────

/**
 * Response body for `POST /api/market/orders/:orderNumber/reviews/reply`
 * (the backend wraps the result in `{ data: ... }` — same envelope as
 * `submitReview`). Mirrors `RespondToReviewResult` in
 * `backend/src/services/review.service.ts` VERBATIM.
 */
export interface RespondToReviewResponse {
  reviewId: string;
  orderNumber: string;
  sellerReply: SellerReply;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * POST /api/market/orders/:orderNumber/reviews/reply — seller posts a
 * single public response to a `buyer_to_seller` review (S2-H9).
 *
 * Auth: `requireAuth` (router-level — `apiFetch` sends the bearer token
 * automatically). The 30-day window + 1-reply invariant + caller-is-seller
 * check are enforced in `review.service.respondToReview` (the frontend is
 * a thin pass-through).
 *
 * @param orderNumber  The order the review belongs to (path param).
 * @param text         Seller's reply (1-500 chars — pre-trimmed by the
 *                     caller; backend Zod validates again).
 * @param signal       Optional AbortSignal for cancelation.
 * @returns            The reviewId + orderNumber + new sellerReply subdoc.
 *
 * @throws {ApiClientError} 400 `VALIDATION_ERROR` when text length is
 *                          outside 1-500 chars (Zod).
 * @throws {ApiClientError} 403 `REVIEW_REPLY_NOT_YOURS` when the caller is
 *                          not the seller of the order.
 * @throws {ApiClientError} 404 `ORDER_NOT_FOUND` / `REVIEW_NOT_FOUND`.
 * @throws {ApiClientError} 409 `REVIEW_REPLY_ALREADY_EXISTS` when a reply
 *                          already exists (or concurrent CAS miss).
 * @throws {ApiClientError} 409 `REVIEW_REPLY_WINDOW_EXPIRED` when the
 *                          30-day reply window has closed.
 * @throws {ApiClientError} 403 `MARKET_REVIEWS_DISABLED` when the feature
 *                          flag is off.
 *
 * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item H-9 (seller
 *           reply reviews — fix de raíz).
 */
export async function respondToReview(
  orderNumber: string,
  text: string,
  signal?: AbortSignal,
): Promise<RespondToReviewResponse> {
  return apiFetch<RespondToReviewResponse>(
    `/market/orders/${encodeURIComponent(orderNumber)}/reviews/reply`,
    {
      method: 'POST',
      body: JSON.stringify({ text }),
      signal,
    },
  );
}

// Re-export ApiClientError so callers can do `import { respondToReview,
// ApiClientError } from '@/lib/market-reviews-api'` without a second
// import from `api-client` (single import surface for the reply flow).
export { ApiClientError };
